import { useMemo, useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { ChatMessage } from "@/types";
import { useChatStore } from "@/stores/chatStore";
import { StreamingCursor } from "./StreamingCursor";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { EntityChip } from "./EntityChip";
import { MessageActions } from "./MessageActions";
import { CodeBlockCopy } from "./CodeBlockCopy";
import { RichBlock } from "./RichBlock";
import { RICH_BLOCK_LANGUAGES } from "./rich-blocks/parse";
import { LimitReachedCard } from "@/features/upgrade/LimitReachedCard";
import { useStreamingBlocks } from "@/hooks/useStreamingBlocks";
import { BlockPlaceholder } from "./rich-blocks/BlockPlaceholder";
import type { Breakpoint } from "@/hooks/useBreakpoint";

/** Clean up markdown artifacts that leak through when Claude doesn't format perfectly. */
function cleanMarkdown(text: string): string {
  // Fix heading markers stuck to preceding text (e.g. "once.#### Both" → "once.\n\n#### Both")
  let clean = text.replace(/([^\n])(#{1,6}\s)/g, "$1\n\n$2");
  // Remove orphaned heading markers that aren't at start of line (e.g. inline "####" with no space)
  clean = clean.replace(/([^\n#])#{2,6}(?=\s)/g, "$1");
  return clean;
}

// Match Combify entity URLs: .../property/ID, .../project/SLUG, .../permit/ID, .../tender/ID
const ENTITY_URL_PATTERN =
  /(?:map\.combify\.com|staging\.eu-west-1\.combify\.com|localhost(?::\d+)?)\/(property|project|permit|tender)\/([^\s)]+)/;


interface MessageBubbleProps {
  message: ChatMessage;
  isLast?: boolean;
  breakpoint?: Breakpoint;
  onEntityClick?: (entityType: string, entityId: string, name: string) => void;
  onRetry?: () => void;
  onEdit?: (newContent: string) => void;
}

export function MessageBubble({ message, isLast, onEntityClick, onRetry, onEdit }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isStreaming = useChatStore((s) => s.isStreaming);
  const activeToolCount = useChatStore((s) => s.activeTools.length);
  const thinkingStepCount = useChatStore((s) => s.thinkingSteps.length);
  const isEmptyStreaming = isLast && !isUser && isStreaming && message.content.length === 0 && activeToolCount === 0 && thinkingStepCount === 0;
  const showCursor = isLast && !isUser && isStreaming && message.content.length > 0;
  const { activeBlock } = useStreamingBlocks(message.content, showCursor ?? false);
  const showActions = !isUser && message.content.length > 0 && !isStreaming;

  // Edit state for user messages
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content);
  const editRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      editRef.current.selectionStart = editRef.current.value.length;
      editRef.current.style.height = "auto";
      editRef.current.style.height = `${Math.min(editRef.current.scrollHeight, 200)}px`;
    }
  }, [isEditing]);

  // Memoize markdown components to avoid re-renders
  const markdownComponents = useMemo<Components>(
    () => ({
      pre: ({ children }) => {
        // Extract language from <code className="language-xxx">
        let language: string | undefined;
        let rawText: string | undefined;
        const child = Array.isArray(children) ? children[0] : children;
        if (child && typeof child === "object" && "props" in child) {
          const props = child as { props: { className?: string; children?: string } };
          const cls = props.props.className;
          if (cls) {
            const match = cls.match(/language-(\S+)/);
            if (match) language = match[1];
          }
          if (typeof props.props.children === "string") {
            rawText = props.props.children;
          }
        }

        // Route rich block languages to RichBlock
        if (language && RICH_BLOCK_LANGUAGES.has(language) && rawText !== undefined) {
          return <RichBlock language={language} content={rawText} onEntityClick={onEntityClick} />;
        }

        return <CodeBlockCopy language={language}>{children}</CodeBlockCopy>;
      },
      table: ({ children }) => (
        <div className="cx-table-scroll">
          <table>{children}</table>
        </div>
      ),
      a: ({ href, children }) => {
        if (href && onEntityClick) {
          const match = href.match(ENTITY_URL_PATTERN);
          if (match) {
            const entityType = match[1]!;
            const entityId = match[2]!;
            return (
              <EntityChip entityType={entityType} entityId={entityId} onClick={onEntityClick}>
                {children}
              </EntityChip>
            );
          }
        }
        // Regular link — open in new tab
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      },
    }),
    [onEntityClick],
  );

  // Limit reached card — renders instead of normal message (after hooks)
  if (message.type === "limit_reached") {
    return (
      <div className="animate-cx-message-in">
        <LimitReachedCard />
      </div>
    );
  }

  if (isUser) {
    const canEdit = !!onEdit && !isStreaming;

    function handleEditSubmit() {
      const trimmed = editValue.trim();
      if (trimmed && trimmed !== message.content && onEdit) {
        onEdit(trimmed);
      }
      setIsEditing(false);
    }

    return (
      <div className="flex justify-end animate-cx-message-in group/msg" aria-label="You">
        <div className="flex flex-col items-end max-w-[75%]">
          <div className="cx-user-bubble px-4 py-3 text-[15px] w-full">
            {isEditing ? (
              <textarea
                ref={editRef}
                className="cx-edit-textarea"
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value);
                  if (editRef.current) {
                    editRef.current.style.height = "auto";
                    editRef.current.style.height = `${Math.min(editRef.current.scrollHeight, 200)}px`;
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleEditSubmit();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditValue(message.content);
                    setIsEditing(false);
                  }
                }}
                onBlur={() => {
                  setEditValue(message.content);
                  setIsEditing(false);
                }}
              />
            ) : (
              <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
            )}
          </div>
          {canEdit && !isEditing && (
            <div className="opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150 mt-1">
              <MessageActions
                content={message.content}
                onEdit={() => {
                  setEditValue(message.content);
                  setIsEditing(true);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isEmptyStreaming) {
    return (
      <div className="animate-cx-message-in" aria-label="Ortvis">
        <ThinkingIndicator />
      </div>
    );
  }

  return (
    <div className="animate-cx-message-in group/msg" aria-label="Ortvis">
      <div className="prose-cx">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {cleanMarkdown(message.content)}
        </ReactMarkdown>
        {showCursor && <StreamingCursor />}
        {activeBlock && <BlockPlaceholder type={activeBlock} />}
      </div>
      {showActions && (
        <div className="opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150 mt-1">
          <MessageActions content={message.content} onRetry={isLast ? onRetry : undefined} showFeedback />
        </div>
      )}
    </div>
  );
}
